export interface IInput {
  error?: string;
  helpMessage?: string;
  type?: string;
  label?: string;
  id?: string;
  disabled?: boolean;
}

import React, { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import Label from "@/components/Label";
import { cva } from "class-variance-authority";
import { Eye, EyeOff } from "lucide-react";

export interface JInputTextProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "type">,
    IInput {
  className?: string;
}

const Input: React.FC<JInputTextProps> = ({
  label,
  id,
  error,
  helpMessage,
  type = "text",
  className,
  ...props
}) => {
  const [typeField, setTypeField] = useState(type);
  const isPasswordType = useMemo(() => type === "password", [type]);

  const isThereError = useMemo(() => error !== undefined, [error]);
  const classError = useMemo(
    () => ({
      "text-red-500 dark:text-red-300 font-medium": error !== undefined,
    }),
    [error]
  );

  const inputVariants = cva(
    `flex h-13 w-full rounded-md border border-border font-medium bg-card px-3 text-normal
      ring-offset-body file:border-0 file:bg-transparent file:text-foreground file:text-sm file:font-medium 
      placeholder:text-muted-foreground focus-visible:outline-none 
      focus-visible:ring focus-visible:ring-card focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50`,
    {
      variants: {},
      defaultVariants: {},
    }
  );

  const toggleTypeInput = () => {
    setTypeField((current) => (current === "password" ? "text" : "password"));
  };

  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="grid w-full items-center gap-2">
      {label && (
        <Label htmlFor={id} className={cn(classError)}>
          {label}
        </Label>
      )}
      <div className="relative">
        <input
          id={inputId}
          aria-describedby={error || helpMessage ? `${id}-help` : undefined}
          aria-invalid={isThereError ? "true" : "false"}
          className={cn(
            inputVariants(),
            {
              "border-red-500 dark:border-red-300 placeholder:text-danger placeholder:opacity-40 placeholder:italic":
                isThereError,
            },
            { "focus-visible:ring-danger": isThereError },
            className
          )}
          type={typeField}
          {...props}
        />
        {isPasswordType && (
          <button
            type="button"
            aria-label={
              typeField === "password" ? "Show password" : "Hide password"
            }
            className={cn(
              "cursor-pointer absolute inset-y-0 right-2 top-1.5 text-primary-fg",
              classError
            )}
            title="Toggle password visibility"
            onClick={toggleTypeInput}
          >
            {typeField !== "password" ? <Eye /> : <EyeOff />}
          </button>
        )}
      </div>
      {error && (
        <div id={`${id}-error`} className={cn(classError)}>
          {error}
        </div>
      )}
      {helpMessage && (
        <div id={`${id}-help`} className={cn(classError)}>
          {helpMessage}
        </div>
      )}
    </div>
  );
};

Input.displayName = "Input";

export default Input;
